import React, { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import { AlertTriangle, History, RefreshCw } from "lucide-react";
import {
  mdiArrowULeftTop,
  mdiBackupRestore,
  mdiCheckCircleOutline,
  mdiChevronDown,
  mdiChevronRight,
  mdiClipboardCheckOutline,
  mdiClipboardListOutline,
  mdiCloseCircleOutline,
  mdiEyeOutline,
  mdiMapMarkerOutline,
  mdiPencilBoxOutline,
  mdiRadioactiveCircleOutline,
  mdiTruckDeliveryOutline,
  mdiUndoVariant,
} from "@mdi/js";
import {
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import { formatDateYYYYMMDD } from "../../content_panel/utils/helpers";
import { useWarehouseOutward } from "../contexts/OutwardContext";
import {
  fetchPost,
  fetchPut,
} from "../../content_panel/services/serviceClient";
import { BulkApproveSTFGApi, CancelOutwardApi } from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../utils/enums";
import OutwardStageModal from "./outward_modal";
import RevertOptionsModal from "./revert_options_modal";
import BulkPodUploadModal from "./bulk_pod_upload_modal";
import DelhiverySyncAllModal from "./delhivery_sync_all_modal";
import SyncLrNumbersModal from "./sync_lr_numbers_modal";
import StockTransferViewModal from "../stock-transfer/stock_transfer_view_modal";
import FillDraftReplacementSTModal from "../stock-transfer/outward_view/FillDraftReplacementSTModal";
import DeleteRejectedSTModal from "./DeleteRejectedSTModal";
import EditRejectedSTModal from "./EditRejectedSTModal";
import {
  getOutwardDestinationLabel,
  getOutwardSourceLabel,
  getStatusStyle,
} from "../warehouse_helper";
import PaginationInfo from "../common-components/PaginationInfo";
import { MobileCard, MobileField } from "../common-components/MobileCard";
import WorkflowTimelineRow from "../workflow/WorkflowTimelineRow";
import {
  getWorkflowTimelinesBatch,
  WorkflowEvent,
} from "../workflow/workflow_repo";
import MarkInsufficientInventoryModal from "../workflow/MarkInsufficientInventoryModal";
import ConfirmationModal from "../common-components/ConfirmationModal";
import ApproveOrCancelSTFGModal from "./ApproveOrCancelSTFGModal";
import RejectSTModal from "../finance/RejectSTModal";
import ReturnOutwardModal from "../finance/ReturnOutwardModal";

interface OutwardOrdersTableProps {
  updatesAllowed?: boolean;
  title?: string;
  subTitle?: string;
  icon?: string;
  showReturnableOverdueTag?: boolean;
  cascade?: boolean;
  cascadeHistory?: boolean;
}

const getTotalValue = (o: WarehouseOutwardModel): number =>
  o.items?.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) ??
  0;

const formatOptionalDateYYYYMMDD = (
  date?: Date | string | number | null,
): string => {
  if (!date) return "";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return parsedDate.toISOString().split("T")[0];
};

interface ItemsSubTableProps {
  title: string;
  rows: Array<{
    itemName?: string;
    inventoryItemCode?: string;
    requestedQty?: number;
    itemPrice?: number;
  }>;
  rowKeyPrefix: string;
  isItemMatch: (it: {
    itemName?: string;
    inventoryItemCode?: string;
  }) => boolean;
}

const ItemsSubTable: React.FC<ItemsSubTableProps> = ({
  title,
  rows,
  rowKeyPrefix,
  isItemMatch,
}) => (
  <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
    <div className="px-4 py-2 text-sm font-semibold text-gray-700 border-b bg-gray-50">
      {title} ({rows.length})
    </div>
    <table className="w-full text-sm">
      <thead className="bg-white border-b">
        <tr>
          <th className="px-4 py-2 text-left font-semibold text-gray-600">
            Item Name
          </th>
          <th className="px-4 py-2 text-left font-semibold text-gray-600">
            Item Code
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Qty
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Unit Price
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Subtotal
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((it, idx) => {
          const qty = it.requestedQty ?? 0;
          const price = it.itemPrice ?? 0;
          const subtotal = qty * price;
          const matched = isItemMatch(it);
          const cellBg = matched ? "bg-green-100" : "";
          return (
            <tr
              key={`${rowKeyPrefix}-${idx}`}
              className={`border-b last:border-b-0 ${
                matched ? "bg-green-100 border-l-4 border-l-green-500" : ""
              }`}
            >
              <td className={`px-4 py-2 text-gray-800 ${cellBg}`}>
                {it.itemName || "--"}
              </td>
              <td className={`px-4 py-2 text-gray-500 ${cellBg}`}>
                {it.inventoryItemCode || "--"}
              </td>
              <td
                className={`px-4 py-2 text-right font-semibold text-gray-800 ${cellBg}`}
              >
                {qty}
              </td>
              <td className={`px-4 py-2 text-right text-gray-700 ${cellBg}`}>
                ₹{price.toLocaleString()}
              </td>
              <td
                className={`px-4 py-2 text-right font-semibold text-gray-800 ${cellBg}`}
              >
                ₹{subtotal.toLocaleString()}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

interface ActionButtonConfig {
  condition: (row: WarehouseOutwardModel) => boolean;
  icon: string;
  color: string;
  onClick: (row: WarehouseOutwardModel) => void;
  disabled?: boolean;
  tooltip?: string;
}

const getActionButtons = (
  row: WarehouseOutwardModel,
  updatesAllowed: boolean,
  openModal: (type: "view" | "stage", outward: WarehouseOutwardModel) => void,
  setSelectedOutward: (row: WarehouseOutwardModel) => void,
  setOpenEditRejectedST: (state: boolean) => void,
  setOpenCancelRejectedST: (state: boolean) => void,
  onRevertSO: (row: WarehouseOutwardModel) => void,
  canRevertSO: boolean,
  onDeallocateST: (row: WarehouseOutwardModel) => void,
  canDeallocateST: boolean,
  onCancelOutward: (row: WarehouseOutwardModel) => void,
  canCancelOutward: boolean,
  onBlockedWarehouseAssignment: () => void,
  onApproveSTFG: (row: WarehouseOutwardModel) => void,
  canApproveSTFG: boolean,
  onReturnOutward: (row: WarehouseOutwardModel) => void,
  onFillDraft: (row: WarehouseOutwardModel) => void,
): ActionButtonConfig[] => {
  const buttons: ActionButtonConfig[] = [
    {
      condition: () => true,
      icon: mdiEyeOutline,
      color: "text-gray-600",
      tooltip: "View",
      onClick: (r) => openModal("view", r),
    },
    // Cancel is allowed for finance users even when updatesAllowed=false,
    // so it lives outside the updatesAllowed gate. canCancelOutward already
    // restricts it to users with finance-module permission.
    {
      condition: (r) =>
        canCancelOutward &&
        r.status === WarehouseOutwardStatus.READY_TO_DISPATCH,
      icon: mdiCloseCircleOutline,
      color: "text-red-600",
      tooltip: "Cancel",
      onClick: (r) => onCancelOutward(r),
    },
    // Return follows Dispatched, mirroring Cancel: available regardless of
    // updatesAllowed, gated on the same finance-module permission.
    {
      condition: (r) =>
        canCancelOutward && r.status === WarehouseOutwardStatus.DISPATCHED,
      icon: mdiUndoVariant,
      color: "text-orange-600",
      tooltip: "Mark as Returned",
      onClick: (r) => onReturnOutward(r),
    },
    // Also outside the updatesAllowed gate: the Replacement tab renders with
    // updatesAllowed=false (it doesn't use the generic WH-assign/allocate/
    // dispatch status actions below), so this needs to live here to show up.
    {
      condition: (r) =>
        r.type === "ST" &&
        r.st_subtype === "Replacement" &&
        r.status === WarehouseOutwardStatus.DRAFT,
      icon: mdiPencilBoxOutline,
      color: "text-blue-600",
      tooltip: "Fill Draft ST",
      onClick: (r) => onFillDraft(r),
    },
  ];

  if (!updatesAllowed) return buttons;

  const statusActions: ActionButtonConfig[] = [
    {
      condition: (r) =>
        r.type === "SO" &&
        r.status === WarehouseOutwardStatus.WH_YET_TO_BE_ASSIGNED,
      icon: mdiMapMarkerOutline,
      color: "text-blue-600",
      tooltip: "Assign Warehouse",
      onClick: (r) => {
        if (r.sync_pending) {
          onBlockedWarehouseAssignment();
          return;
        }
        openModal("stage", r);
      },
    },
    {
      condition: (r) =>
        canApproveSTFG &&
        r.type === "ST" &&
        r.status === WarehouseOutwardStatus.ST_FG_APPROVAL_PENDING &&
        (r.items ?? []).some((it) => it.subCategory === "Finished Goods"),
      icon: mdiClipboardCheckOutline,
      color: "text-emerald-600",
      tooltip: "Approve FG ST",
      onClick: (r) => onApproveSTFG(r),
    },
    {
      condition: (r) =>
        r.status === WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING,
      icon: mdiRadioactiveCircleOutline,
      color: "text-blue-600",
      tooltip: "Allocate Devices",
      onClick: (r) => openModal("stage", r),
    },
    {
      condition: (r) =>
        r.status === WarehouseOutwardStatus.MATERIAL_PICKING_PENDING,
      icon: mdiClipboardListOutline,
      color: "text-cyan-600",
      tooltip: "Confirm Picking",
      onClick: (r) => openModal("stage", r),
    },
    {
      condition: (r) => r.status === WarehouseOutwardStatus.READY_TO_DISPATCH,
      icon: mdiTruckDeliveryOutline,
      color: "text-blue-600",
      tooltip: "Dispatch",
      onClick: (r) => openModal("stage", r),
    },
    {
      condition: (r) => r.status === WarehouseOutwardStatus.TRACKING_ID_PENDING,
      icon: mdiTruckDeliveryOutline,
      color: "text-blue-600",
      tooltip: "Add Tracking ID",
      onClick: (r) => openModal("stage", r),
    },
    {
      condition: (r) => r.status === WarehouseOutwardStatus.DISPATCHED,
      icon: mdiCheckCircleOutline,
      color: "text-green-600",
      tooltip: "Mark as Delivered",
      onClick: (r) => openModal("stage", r),
    },
    {
      condition: (r) =>
        r.type === "ST" &&
        r.st_subtype === "Regular" &&
        r.status === WarehouseOutwardStatus.REJECTED,
      icon: mdiPencilBoxOutline,
      color: "text-blue-600",
      tooltip: "Edit Rejected",
      onClick: (r) => {
        setSelectedOutward(r);
        setOpenEditRejectedST(true);
      },
    },
    {
      condition: (r) =>
        r.type === "ST" &&
        r.st_subtype === "Regular" &&
        r.status === WarehouseOutwardStatus.REJECTED,
      icon: mdiCloseCircleOutline,
      color: "text-red-600",
      tooltip: "Cancel Rejected",
      onClick: (r) => {
        setSelectedOutward(r);
        setOpenCancelRejectedST(true);
      },
    },
    {
      condition: (r) =>
        canRevertSO &&
        r.type === "SO" &&
        (r.status === WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING ||
          r.status === WarehouseOutwardStatus.DOC_PENDING),
      icon: mdiBackupRestore,
      color: "text-amber-600",
      tooltip: "Revert outward",
      onClick: (r) => onRevertSO(r),
    },
    {
      condition: (r) =>
        canDeallocateST &&
        r.type === "ST" &&
        r.status === WarehouseOutwardStatus.DOC_PENDING &&
        (r.items ?? []).some((it) => it.subCategory === "Finished Goods"),
      icon: mdiArrowULeftTop,
      color: "text-amber-600",
      tooltip: "Deallocate Devices",
      onClick: (r) => onDeallocateST(r),
    },
  ];

  return [...buttons, ...statusActions];
};

const OutwardOrdersTable: React.FC<OutwardOrdersTableProps> = ({
  updatesAllowed = true,
  title,
  subTitle,
  icon,
  showReturnableOverdueTag = false,
  cascade = false,
  cascadeHistory = false,
}) => {
  const {
    outwardList,
    modals,
    selectedOutward,
    setSelectedOutward,
    closeModal,
    openModal,
    refreshOutwards,
    filters,
    setFilters,
    page,
    pagination,
  } = useWarehouseOutward();

  const itemSearchTokens = (filters.itemSearch || "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const isItemMatch = (it: { itemName?: string; inventoryItemCode?: string }) =>
    itemSearchTokens.length > 0 &&
    itemSearchTokens.some(
      (tok) =>
        (it.itemName || "").toLowerCase().includes(tok) ||
        (it.inventoryItemCode || "").toLowerCase().includes(tok),
    );

  const sortBy = filters.sortBy;
  const sortOrder = filters.sortOrder;
  const cycleSortByValue = () => {
    setFilters((p) => {
      if (p.sortBy !== "value")
        return { ...p, sortBy: "value", sortOrder: "asc" };
      if (p.sortOrder === "asc")
        return { ...p, sortBy: "value", sortOrder: "desc" };
      return { ...p, sortBy: undefined, sortOrder: undefined };
    });
  };
  const sortArrow =
    sortBy === "value" ? (sortOrder === "asc" ? "▲" : "▼") : "↕";

  const [openEditRejectedST, setOpenEditRejectedST] = useState(false);
  const [openCancelRejectedST, setOpenCancelRejectedST] = useState(false);
  const [openBulkPod, setOpenBulkPod] = useState(false);
  const [openDelhiverySync, setOpenDelhiverySync] = useState(false);
  const [openSyncLr, setOpenSyncLr] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [timelineRows, setTimelineRows] = useState<Set<string>>(new Set());
  const [insufficientModalFor, setInsufficientModalFor] =
    useState<WarehouseOutwardModel | null>(null);
  const toggleTimeline = (id: string) => {
    setTimelineRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [revertingOrderId, setRevertingOrderId] = useState<string | null>(null);
  const [reassigningOrderId, setReassigningOrderId] = useState<string | null>(
    null,
  );
  const [deallocatingSTId, setDeallocatingSTId] = useState<string | null>(null);
  const [approvingSTFGId, setApprovingSTFGId] = useState<string | null>(null);
  const [cancellingOutwardId, setCancellingOutwardId] = useState<string | null>(
    null,
  );
  const [revertConfirmFor, setRevertConfirmFor] =
    useState<WarehouseOutwardModel | null>(null);
  const [approveSTFGFor, setApproveSTFGFor] =
    useState<WarehouseOutwardModel | null>(null);
  const [rejectSTFor, setRejectSTFor] = useState<WarehouseOutwardModel | null>(
    null,
  );
  const [returnOutwardFor, setReturnOutwardFor] =
    useState<WarehouseOutwardModel | null>(null);
  const [fillDraftRow, setFillDraftRow] =
    useState<WarehouseOutwardModel | null>(null);
  const { showToast } = useToast();
  const { isAuthorised, user } = useAuth();
  // ST FG approval is restricted to holders of the ORDER_APPROVER role
  // (mirrors the backend check in BulkApproveSTFGController).
  const canApproveSTFG = (user?.emp_profile?.permissions ?? []).some(
    (r: any) => r?.title === "ORDER_APPROVER",
  );
  const canRevertSO = isAuthorised({
    module: MODULE.warehouse_revert_order,
    action: [ACTION.CREATE, ACTION.UPDATE],
    operator: "and",
  });
  const canDeallocateST = canRevertSO;
  const canCancelOutward = isAuthorised({
    module: MODULE.warehouse_module_finance,
    action: [ACTION.READ, ACTION.CREATE, ACTION.UPDATE],
    operator: "or",
  });

  const [revertOptionsRow, setRevertOptionsRow] =
    useState<WarehouseOutwardModel | null>(null);

  const handleRevertSO = (row: WarehouseOutwardModel) => {
    if (row.type !== "SO" || !row.order_id) return;
    if (revertingOrderId || reassigningOrderId) return;
    setRevertOptionsRow(row);
  };

  const executeRevertForOrderUpdate = async (row: WarehouseOutwardModel) => {
    if (row.type !== "SO" || !row.order_id || revertingOrderId) return;
    setRevertingOrderId(row.order_id);
    const res = await fetchPost(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/revert-so-outwards-to-wh-yet-to-be-assigned`,
      { order_ids: [row.order_id] },
    );
    setRevertingOrderId(null);
    setRevertConfirmFor(null);
    setRevertOptionsRow(null);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: res.message || "Outward reverted to WH Yet To Be Assigned",
      });
      refreshOutwards();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to revert outward",
      });
    }
  };

  const executeReassignWarehouseOnly = async (row: WarehouseOutwardModel) => {
    if (row.type !== "SO" || !row.order_id || reassigningOrderId) return;
    setReassigningOrderId(row.order_id);
    const res = await fetchPost(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/reassign-warehouse-only`,
      { order_ids: [row.order_id] },
    );
    setReassigningOrderId(null);
    setRevertOptionsRow(null);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: res.message || "Warehouse released; ready for re-assignment",
      });
      refreshOutwards();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to reassign warehouse",
      });
    }
  };

  const handleCancelOutward = async (row: WarehouseOutwardModel) => {
    if (!row.id) return;
    if (cancellingOutwardId) return;

    const label = row.type === "ST" ? "stock transfer" : "order";
    const confirmed = window.confirm(
      `Cancel this ${label} (${row.id})? Allocated device movements${
        row.type === "ST" ? " and in-transit entries" : ""
      } will be removed.`,
    );
    if (!confirmed) return;

    setCancellingOutwardId(row.id);
    const res = await fetchPut(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/cancel-outward/${row.id}`,
      {},
    );
    setCancellingOutwardId(null);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text:
          row.type === "ST"
            ? "Stock transfer cancelled" +
              (res.message?.match(/ Linked Reverse DC.*$/)?.[0] ?? "")
            : "Order cancelled",
      });
      refreshOutwards();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to cancel outward",
      });
    }
  };

  const handleApproveSTFG = (row: WarehouseOutwardModel) => {
    if (row.type !== "ST" || !row.id) return;
    if (approvingSTFGId || cancellingOutwardId) return;
    setApproveSTFGFor(row);
  };

  const executeApproveSTFG = async (row: WarehouseOutwardModel) => {
    if (row.type !== "ST" || !row.id) return;
    if (approvingSTFGId) return;

    setApprovingSTFGId(row.id);
    const res = await BulkApproveSTFGApi([row.id]);
    setApprovingSTFGId(null);

    const skipped = res?.data?.skipped ?? [];
    if (res?.success && skipped.length === 0) {
      showToast({
        type: ToastType.success,
        text: `FG approved; ${row.id} moved to Device Allocation Pending`,
      });
      setApproveSTFGFor(null);
      refreshOutwards();
    } else if (res?.success && skipped.length) {
      showToast({
        type: ToastType.error,
        text: skipped[0]?.reason || "Approval skipped",
      });
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to approve FG ST",
      });
    }
  };

  const executeCancelSTFromApproveModal = async (
    row: WarehouseOutwardModel,
  ) => {
    if (!row.id) return;
    if (cancellingOutwardId) return;

    setCancellingOutwardId(row.id);
    const res = await CancelOutwardApi(row.id);
    setCancellingOutwardId(null);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: "Stock transfer cancelled",
      });
      setApproveSTFGFor(null);
      refreshOutwards();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to cancel stock transfer",
      });
    }
  };

  const handleDeallocateST = async (row: WarehouseOutwardModel) => {
    if (row.type !== "ST" || !row.id) return;
    if (deallocatingSTId) return;

    const confirmed = window.confirm(
      `Deallocate devices on ${row.id} and move it back to "Device Allocation Pending"? Allocated device movements and in-transit entries will be removed.`,
    );
    if (!confirmed) return;

    setDeallocatingSTId(row.id);
    const res = await fetchPut(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/deallocate-ST/${row.id}`,
      {},
    );
    setDeallocatingSTId(null);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: "Devices deallocated; ST moved to Device Allocation Pending",
      });
      refreshOutwards();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.error || "Failed to deallocate ST devices",
      });
    }
  };

  useEffect(() => {
    setExpandedRows(new Set());
  }, [cascade]);

  useEffect(() => {
    setTimelineRows(new Set());
  }, [cascadeHistory]);

  // Cache of timelines keyed by the entity_id used in the workflow events
  // (= order_id for SO, outward.id for ST). Persists across cascadeHistory
  // off→on toggles on the same page; cleared when the visible row set changes.
  const [timelinesByEntity, setTimelinesByEntity] = useState<
    Record<string, WorkflowEvent[]>
  >({});
  const [historyBatchLoading, setHistoryBatchLoading] = useState(false);

  const resolveEntityId = (row: WarehouseOutwardModel): string =>
    row.type === "SO" ? (row.order_id ?? row.id) : row.id;

  const visibleEntityIds = useMemo(
    () => (outwardList ?? []).map(resolveEntityId).filter(Boolean),
    [outwardList],
  );

  useEffect(() => {
    setTimelinesByEntity({});
  }, [visibleEntityIds.join("|")]);

  useEffect(() => {
    // The desktop table and mobile card are both always mounted (CSS-toggled
    // via hidden md:block / md:hidden, not conditionally rendered), so a
    // manually-toggled row's WorkflowTimelineRow mounts twice. Routing every
    // needed entity through this one shared batch fetch — cascadeHistory rows
    // AND manually-toggled rows alike — means both instances get the same
    // prefetchedEvents and neither falls back to its own per-entity fetch,
    // which is what caused the timeline endpoint to be hit twice on a click.
    const neededIds = cascadeHistory
      ? visibleEntityIds
      : (outwardList ?? [])
          .filter((row) => timelineRows.has(row.id))
          .map(resolveEntityId)
          .filter(Boolean);
    const missing = neededIds.filter((id) => !(id in timelinesByEntity));
    if (!missing.length) return;
    let cancelled = false;
    setHistoryBatchLoading(true);
    getWorkflowTimelinesBatch(missing).then((res) => {
      if (cancelled) return;
      if (res?.success && res.data) {
        setTimelinesByEntity((prev) => ({ ...prev, ...res.data! }));
      }
      setHistoryBatchLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    cascadeHistory,
    visibleEntityIds,
    timelinesByEntity,
    timelineRows,
    outwardList,
  ]);

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (outwardList.length === 0) {
    return (
      <div className="rounded-lg border shadow-sm bg-white p-8 text-center">
        <p className="text-gray-500">No outward orders found</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border shadow-sm bg-white">
        <div className=" flex flex-col gap-1 p-6 pb-3">
          <div className="flex items-center justify-between">
            <h3 className=" flex flex-row gap-1 text-base font-semibold">
              {icon && <Icon path={icon} size={1} />}
              {title ? title : "Outward Orders"}
            </h3>
            <div className="flex items-center gap-3">
              {updatesAllowed && (
                <button
                  type="button"
                  id="warehouse-outward-sync-delhivery-btn"
                  onClick={() => setOpenDelhiverySync(true)}
                  className="h-8 px-3 rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 text-xs font-medium"
                >
                  Sync All Delhivery
                </button>
              )}
              {updatesAllowed && (
                <button
                  type="button"
                  id="warehouse-outward-sync-lr-btn"
                  onClick={() => setOpenSyncLr(true)}
                  className="h-8 px-3 rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 text-xs font-medium"
                >
                  Sync LR Numbers
                </button>
              )}
              {updatesAllowed && (
                <button
                  type="button"
                  id="warehouse-outward-bulk-pod-btn"
                  onClick={() => setOpenBulkPod(true)}
                  className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
                >
                  Bulk Upload POD
                </button>
              )}
              <PaginationInfo
                totalRecords={pagination.total}
                currentPage={page}
                limit={pagination.limit}
              />
            </div>
          </div>
          {subTitle && <div className="text-sm text-gray-600 ">{subTitle}</div>}
        </div>

        <div className="p-6 pt-0 overflow-x-auto hidden md:block">
          <table
            id="warehouse-outward-orders-table"
            className="w-full min-w-max text-sm"
          >
            <thead className="border-b top-0 bg-white">
              <tr>
                <th className="p-3 text-left left-0 bg-white text-muted-foreground">
                  Order ID
                </th>
                <th className="p-3 text-left text-muted-foreground">Type</th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap">
                  Created By
                </th>
                <th className="p-3 text-left text-muted-foreground whitespace-nowrap">
                  Created At
                </th>
                <th className="p-3 text-left md:table-cell text-muted-foreground">
                  From
                </th>
                <th className="p-3 text-left lg:table-cell text-muted-foreground">
                  Destination
                </th>
                <th className="p-3 text-right text-muted-foreground">
                  <button
                    type="button"
                    id="warehouse-outward-sort-value-btn"
                    onClick={cycleSortByValue}
                    className="inline-flex items-center gap-1 hover:text-gray-800"
                  >
                    Value
                    <span
                      className={
                        sortBy === "value" ? "text-blue-600" : "text-gray-400"
                      }
                    >
                      {sortArrow}
                    </span>
                  </button>
                </th>
                <th className="p-3 text-left text-muted-foreground">Status</th>
                <th className="p-3 text-right text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {outwardList.map((row) => {
                const actionButtons = getActionButtons(
                  row,
                  updatesAllowed,
                  (type, outward) => openModal(type, outward),
                  setSelectedOutward,
                  setOpenEditRejectedST,
                  setOpenCancelRejectedST,
                  handleRevertSO,
                  canRevertSO,
                  handleDeallocateST,
                  canDeallocateST,
                  handleCancelOutward,
                  canCancelOutward,
                  () =>
                    showToast({
                      type: ToastType.warning,
                      text: "Re-approve SO before assigning warehouse",
                    }),
                  handleApproveSTFG,
                  canApproveSTFG,
                  (r) => setReturnOutwardFor(r),
                  (r) => setFillDraftRow(r),
                );

                const isReturnable =
                  "st_subtype" in row && row.st_subtype === "Returnable";
                const deliverdStatus =
                  "status" in row && row.status === "Delivered";

                const expectedDate =
                  isReturnable && "expected_return_date" in row
                    ? row.expected_return_date
                    : "expected_date" in row
                      ? row.expected_date
                      : null;
                const isOverdue =
                  isReturnable &&
                  deliverdStatus &&
                  expectedDate &&
                  new Date() > new Date(expectedDate);

                const items = row.items || [];
                const faultyItems = (row as any).faulty_items || [];
                const hasItems = items.length > 0 || faultyItems.length > 0;
                const hasMatchedItem =
                  items.some(isItemMatch) || faultyItems.some(isItemMatch);
                const isExpanded =
                  expandedRows.has(row.id) || hasMatchedItem || cascade;
                const dispatchedDate = formatOptionalDateYYYYMMDD(
                  row?.dispatched_date || row?.dispatched_at,
                );
                const rowId =
                  row.type === "SO" ? (row.order_id ?? row.id) : row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      id={`warehouse-outward-row-${rowId}`}
                      className="border-b hover:bg-muted/50"
                    >
                      <td className="p-3 font-semibold sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                          <div className="relative group">
                            <button
                              id={`warehouse-outward-row-${rowId}-timeline`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTimeline(row.id);
                              }}
                              className={`h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 ${
                                timelineRows.has(row.id)
                                  ? "text-blue-600"
                                  : "text-gray-500"
                              }`}
                              aria-label="Workflow timeline"
                            >
                              <History className="h-3.5 w-3.5" />
                            </button>
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 rounded-md bg-gray-700 px-1.5 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap z-50">
                              Workflow timeline
                            </span>
                          </div>
                          {hasItems ? (
                            <button
                              id={`warehouse-outward-row-${rowId}-expand`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(row.id);
                              }}
                              className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                              aria-label={
                                isExpanded ? "Collapse items" : "Expand items"
                              }
                            >
                              <Icon
                                path={
                                  isExpanded ? mdiChevronDown : mdiChevronRight
                                }
                                size={0.7}
                              />
                            </button>
                          ) : (
                            <span className="inline-block h-6 w-6" />
                          )}
                          <span>
                            {row.type === "SO" ? row.order_id : row.id}
                          </span>
                          {showReturnableOverdueTag && isOverdue && (
                            <div className="inline-flex items-center rounded-full border py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-red-600 text-white hover:bg-red-700 text-xs px-1.5">
                              Overdue
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="p-3">
                        {row.type === "ST" &&
                        (row.st_subtype === "Returnable" ||
                          row.st_subtype === "Replacement" ||
                          row.st_subtype === "FoC" ||
                          row.st_subtype === "Reverse DC") ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-secondary">
                              {row.type}
                            </span>
                            <div
                              className={`inline-flex items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 gap-1 font-medium text-[10px] px-1.5 py-0 ${
                                row.st_subtype === "Replacement"
                                  ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                  : row.st_subtype === "FoC"
                                    ? "bg-green-100 text-green-600 border-green-300"
                                    : row.st_subtype === "Reverse DC"
                                      ? "bg-rose-100 text-rose-700 border-rose-300"
                                      : "bg-blue-100 text-blue-600 border-blue-300"
                              }`}
                            >
                              <RefreshCw className="h-2.5 w-2.5" />
                              {row.st_subtype}
                            </div>
                          </div>
                        ) : (
                          <span className="rounded bg-secondary px-2 py-0.5 text-xs">
                            {row.type}
                          </span>
                        )}
                      </td>

                      <td className="p-3 md:table-cell text-muted-foreground whitespace-nowrap">
                        {row?.created_by_name ?? "-"}
                      </td>

                      <td className="p-3 md:table-cell text-muted-foreground whitespace-nowrap">
                        {formatDateYYYYMMDD(row?.created_at)}
                      </td>

                      <td className="p-3 md:table-cell">
                        {getOutwardSourceLabel(row)}
                      </td>

                      <td className="p-3 lg:table-cell">
                        {getOutwardDestinationLabel(row)}
                      </td>

                      <td className="p-3 text-right font-medium">
                        ₹{getTotalValue(row).toLocaleString()}
                      </td>

                      <td className="p-3">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className="rounded-full border px-2.5 py-0.5 text-xs"
                            style={getStatusStyle(row.status)}
                          >
                            {row.status}
                          </span>
                          {dispatchedDate && (
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              Dispatched: {dispatchedDate}
                            </span>
                          )}
                          {row.shipping_details?.tracking_status && (
                            <span className="text-xs text-blue-600 whitespace-nowrap">
                              {row.shipping_details.tracking_status}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          {row.status ===
                            WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING && (
                            <div className="relative group">
                              <button
                                id={`warehouse-outward-row-${rowId}-insufficient`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInsufficientModalFor(row);
                                }}
                                className={`h-8 w-8 inline-flex items-center justify-center rounded hover:bg-amber-500 ${
                                  (row as any).insufficient_inventory_remark
                                    ? "text-amber-600"
                                    : "text-gray-500"
                                }`}
                                aria-label="Flag insufficient inventory"
                              >
                                <AlertTriangle className="h-4 w-4 group-hover:text-white" />
                              </button>
                              <span className="pointer-events-none absolute top-full right-0 mt-1 rounded-md bg-gray-700 px-1.5 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap z-20">
                                {(row as any).insufficient_inventory_remark
                                  ? "Update insufficient inventory note"
                                  : "Flag insufficient inventory"}
                              </span>
                            </div>
                          )}
                          {actionButtons
                            .filter((btn) => btn.condition(row))
                            .map((btn, idx) => (
                              <div key={idx} className="relative group">
                                <button
                                  id={`warehouse-outward-row-${rowId}-action-${(
                                    btn.tooltip || `action-${idx + 1}`
                                  )
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                  onClick={() => btn.onClick(row)}
                                  disabled={btn.disabled}
                                  className={`h-8 w-8 inline-flex items-center justify-center rounded hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed ${btn.color}`}
                                  aria-label={
                                    btn.tooltip || `Action ${idx + 1}`
                                  }
                                >
                                  <Icon
                                    path={btn.icon}
                                    size={0.8}
                                    className="group-hover:text-white"
                                  />
                                </button>

                                {btn.tooltip && (
                                  <span
                                    className="
                                    pointer-events-none
                                    absolute top-full left-1/2 -translate-x-1/2 mt-1
                                    rounded-md bg-gray-400 px-1.5 py-1
                                    text-xs text-white
                                    opacity-0 transition-opacity
                                    group-hover:opacity-100
                                    whitespace-nowrap
                                  "
                                  >
                                    {btn.tooltip}
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </td>
                    </tr>

                    {hasItems && isExpanded && (
                      <tr className="border-b bg-gray-50">
                        <td colSpan={9} className="p-4 space-y-3">
                          {items.length > 0 && (
                            <ItemsSubTable
                              title={
                                faultyItems.length > 0
                                  ? "Replacement Items"
                                  : "Items"
                              }
                              rows={items}
                              rowKeyPrefix={`${row.id}-item`}
                              isItemMatch={isItemMatch}
                            />
                          )}
                          {faultyItems.length > 0 && (
                            <ItemsSubTable
                              title="Faulty Items"
                              rows={faultyItems}
                              rowKeyPrefix={`${row.id}-faulty`}
                              isItemMatch={isItemMatch}
                            />
                          )}
                        </td>
                      </tr>
                    )}
                    {(timelineRows.has(row.id) ||
                      (cascadeHistory &&
                        resolveEntityId(row) in timelinesByEntity)) && (
                      <WorkflowTimelineRow
                        entityType={row.type === "SO" ? "SO" : "ST"}
                        entityId={resolveEntityId(row)}
                        colSpan={9}
                        prefetchedEvents={
                          timelinesByEntity[resolveEntityId(row)]
                        }
                        deferToParent
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden p-4 pt-0 space-y-3">
          {outwardList.map((row) => {
            const actionButtons = getActionButtons(
              row,
              updatesAllowed,
              (type, outward) => openModal(type, outward),
              setSelectedOutward,
              setOpenEditRejectedST,
              setOpenCancelRejectedST,
              handleRevertSO,
              canRevertSO,
              handleDeallocateST,
              canDeallocateST,
              handleCancelOutward,
              canCancelOutward,
              () =>
                showToast({
                  type: ToastType.warning,
                  text: "Re-approve SO before assigning warehouse",
                }),
              handleApproveSTFG,
              canApproveSTFG,
              (r) => setReturnOutwardFor(r),
              (r) => setFillDraftRow(r),
            );

            const isReturnable =
              "st_subtype" in row && row.st_subtype === "Returnable";
            const deliverdStatus =
              "status" in row && row.status === "Delivered";
            const expectedDate =
              isReturnable && "expected_return_date" in row
                ? row.expected_return_date
                : "expected_date" in row
                  ? row.expected_date
                  : null;
            const isOverdue =
              isReturnable &&
              deliverdStatus &&
              expectedDate &&
              new Date() > new Date(expectedDate);

            const items = row.items || [];
            const faultyItems = (row as any).faulty_items || [];
            const hasItems = items.length > 0 || faultyItems.length > 0;
            const hasMatchedItem =
              items.some(isItemMatch) || faultyItems.some(isItemMatch);
            const isExpanded =
              expandedRows.has(row.id) || hasMatchedItem || cascade;
            const dispatchedDate = formatOptionalDateYYYYMMDD(
              row?.dispatched_date || row?.dispatched_at,
            );
            const showTimeline =
              timelineRows.has(row.id) ||
              (cascadeHistory && resolveEntityId(row) in timelinesByEntity);
            const rowId = row.type === "SO" ? (row.order_id ?? row.id) : row.id;

            return (
              <MobileCard key={row.id} id={`warehouse-outward-card-${rowId}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1">
                      <p className="font-semibold text-gray-900 break-words">
                        {row.type === "SO" ? row.order_id : row.id}
                      </p>
                      {showReturnableOverdueTag && isOverdue && (
                        <span className="inline-flex items-center rounded-full border py-0.5 font-semibold border-transparent bg-red-600 text-white text-xs px-1.5">
                          Overdue
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-secondary">
                        {row.type}
                      </span>
                      {row.type === "ST" &&
                        (row.st_subtype === "Returnable" ||
                          row.st_subtype === "Replacement" ||
                          row.st_subtype === "FoC" ||
                          row.st_subtype === "Reverse DC") && (
                          <span
                            className={`inline-flex items-center rounded-full border gap-1 font-medium text-[10px] px-1.5 py-0 ${
                              row.st_subtype === "Replacement"
                                ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                : row.st_subtype === "FoC"
                                  ? "bg-green-100 text-green-600 border-green-300"
                                  : row.st_subtype === "Reverse DC"
                                    ? "bg-red-100 text-red-700 border-red-300"
                                    : "bg-blue-100 text-blue-600 border-blue-300"
                            }`}
                          >
                            <RefreshCw className="h-2.5 w-2.5" />
                            {row.st_subtype}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                      className="rounded-full border px-2.5 py-0.5 text-xs"
                      style={getStatusStyle(row.status)}
                    >
                      {row.status}
                    </span>
                    {dispatchedDate && (
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        Dispatched: {dispatchedDate}
                      </span>
                    )}
                    {row.shipping_details?.tracking_status && (
                      <span className="text-xs text-blue-600 whitespace-nowrap">
                        {row.shipping_details.tracking_status}
                      </span>
                    )}
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  <MobileField label="Created By">
                    {row?.created_by_name ?? "-"}
                  </MobileField>
                  <MobileField label="Created At">
                    {formatDateYYYYMMDD(row?.created_at)}
                  </MobileField>
                  <MobileField label="From">
                    {getOutwardSourceLabel(row)}
                  </MobileField>
                  <MobileField label="Destination">
                    {getOutwardDestinationLabel(row)}
                  </MobileField>
                  <MobileField label="Value">
                    ₹{getTotalValue(row).toLocaleString()}
                  </MobileField>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
                  {row.status ===
                    WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING && (
                    <button
                      id={`warehouse-outward-card-${rowId}-insufficient`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setInsufficientModalFor(row);
                      }}
                      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-gray-100 ${
                        (row as any).insufficient_inventory_remark
                          ? "text-amber-600"
                          : "text-gray-700"
                      }`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Insufficient
                    </button>
                  )}
                  {actionButtons
                    .filter((btn) => btn.condition(row))
                    .map((btn, idx) => (
                      <button
                        key={idx}
                        id={`warehouse-outward-card-${rowId}-action-${(
                          btn.tooltip || `action-${idx + 1}`
                        )
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        onClick={() => btn.onClick(row)}
                        disabled={btn.disabled}
                        className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${btn.color}`}
                      >
                        <Icon path={btn.icon} size={0.6} />
                        {btn.tooltip || `Action ${idx + 1}`}
                      </button>
                    ))}
                  <button
                    id={`warehouse-outward-card-${rowId}-timeline`}
                    onClick={() => toggleTimeline(row.id)}
                    className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-gray-100 ${
                      timelineRows.has(row.id)
                        ? "text-blue-600"
                        : "text-gray-700"
                    }`}
                  >
                    <History className="h-3.5 w-3.5" />
                    Timeline
                  </button>
                  {hasItems && (
                    <button
                      id={`warehouse-outward-card-${rowId}-expand`}
                      onClick={() => toggleExpanded(row.id)}
                      className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      <Icon
                        path={isExpanded ? mdiChevronDown : mdiChevronRight}
                        size={0.6}
                      />
                      Items
                    </button>
                  )}
                </div>

                {hasItems && isExpanded && (
                  <div className="mt-2 space-y-3">
                    {items.length > 0 && (
                      <ItemsSubTable
                        title={
                          faultyItems.length > 0 ? "Replacement Items" : "Items"
                        }
                        rows={items}
                        rowKeyPrefix={`${row.id}-m-item`}
                        isItemMatch={isItemMatch}
                      />
                    )}
                    {faultyItems.length > 0 && (
                      <ItemsSubTable
                        title="Faulty Items"
                        rows={faultyItems}
                        rowKeyPrefix={`${row.id}-m-faulty`}
                        isItemMatch={isItemMatch}
                      />
                    )}
                  </div>
                )}

                {showTimeline && (
                  <table className="w-full">
                    <tbody>
                      <WorkflowTimelineRow
                        entityType={row.type === "SO" ? "SO" : "ST"}
                        entityId={resolveEntityId(row)}
                        colSpan={1}
                        prefetchedEvents={
                          timelinesByEntity[resolveEntityId(row)]
                        }
                        deferToParent
                      />
                    </tbody>
                  </table>
                )}
              </MobileCard>
            );
          })}
        </div>
      </div>

      {/* ==================== MODALS ==================== */}
      {modals.view && selectedOutward && (
        <StockTransferViewModal
          open={modals.view}
          onClose={() => {
            closeModal("view");
            // The Replacement ST view has HW/Biz approve-reject actions that
            // change status from inside this modal — refresh so the list
            // reflects it without a manual page reload. Harmless no-op
            // refetch when nothing changed.
            refreshOutwards();
          }}
          outward={selectedOutward}
        />
      )}

      {modals.stage && selectedOutward && (
        <OutwardStageModal
          open={modals.stage}
          outward={selectedOutward}
          stage={selectedOutward.status!}
          onClose={() => closeModal("stage")}
          onSuccess={() => {
            closeModal("stage");
            refreshOutwards();
          }}
          onRefresh={refreshOutwards}
        />
      )}

      {fillDraftRow && (
        <FillDraftReplacementSTModal
          open={!!fillDraftRow}
          outward={fillDraftRow as any}
          onClose={() => setFillDraftRow(null)}
          onSubmitted={() => {
            setFillDraftRow(null);
            refreshOutwards();
          }}
        />
      )}

      {openEditRejectedST && (
        <EditRejectedSTModal
          open={openEditRejectedST}
          onClose={() => setOpenEditRejectedST(false)}
          onSuccess={() => {
            refreshOutwards();
          }}
          selectedOutward={selectedOutward}
        />
      )}

      {openCancelRejectedST && (
        <DeleteRejectedSTModal
          open={openCancelRejectedST}
          onClose={() => setOpenCancelRejectedST(false)}
          selectedOutward={selectedOutward}
        />
      )}

      {insufficientModalFor && (
        <MarkInsufficientInventoryModal
          outwardId={insufficientModalFor.id}
          entityLabel={
            insufficientModalFor.type === "SO"
              ? (insufficientModalFor.order_id ?? insufficientModalFor.id)
              : insufficientModalFor.id
          }
          onClose={() => setInsufficientModalFor(null)}
          onSuccess={() => {
            showToast({
              type: ToastType.success,
              text: "Insufficient inventory remark saved",
            });
            refreshOutwards();
          }}
        />
      )}

      <ConfirmationModal
        show={!!revertConfirmFor}
        type="danger"
        title="Revert outward to WH Yet To Be Assigned"
        message={
          revertConfirmFor
            ? `This deallocates devices and clears the assigned warehouse for order ${(revertConfirmFor as any).order_id}. The action is logged in the workflow timeline and cannot be undone automatically.`
            : ""
        }
        confirmText="Revert"
        cancelText="Cancel"
        isSubmitting={!!revertingOrderId}
        onConfirm={() => {
          if (revertConfirmFor) executeRevertForOrderUpdate(revertConfirmFor);
        }}
        onCancel={() => {
          if (!revertingOrderId) setRevertConfirmFor(null);
        }}
      />

      {openBulkPod && (
        <BulkPodUploadModal
          open={openBulkPod}
          onClose={() => setOpenBulkPod(false)}
          onSuccess={() => refreshOutwards()}
        />
      )}

      {openDelhiverySync && (
        <DelhiverySyncAllModal
          open={openDelhiverySync}
          onClose={() => setOpenDelhiverySync(false)}
          onSuccess={() => refreshOutwards()}
        />
      )}

      {openSyncLr && (
        <SyncLrNumbersModal
          open={openSyncLr}
          onClose={() => setOpenSyncLr(false)}
          onSuccess={() => refreshOutwards()}
        />
      )}

      <RevertOptionsModal
        open={!!revertOptionsRow}
        outward={revertOptionsRow}
        busy={!!revertingOrderId || !!reassigningOrderId}
        onClose={() => setRevertOptionsRow(null)}
        onRevertForOrderUpdate={() => {
          if (revertOptionsRow) {
            setRevertConfirmFor(revertOptionsRow);
            setRevertOptionsRow(null);
          }
        }}
        onReassignWarehouseOnly={() => {
          if (revertOptionsRow) executeReassignWarehouseOnly(revertOptionsRow);
        }}
      />

      <ApproveOrCancelSTFGModal
        open={!!approveSTFGFor}
        outward={approveSTFGFor}
        approving={!!approvingSTFGId && approvingSTFGId === approveSTFGFor?.id}
        cancelling={
          !!cancellingOutwardId && cancellingOutwardId === approveSTFGFor?.id
        }
        onClose={() => {
          if (!approvingSTFGId && !cancellingOutwardId) setApproveSTFGFor(null);
        }}
        onApprove={() => {
          if (approveSTFGFor) executeApproveSTFG(approveSTFGFor);
        }}
        onCancelST={() => {
          if (approveSTFGFor) executeCancelSTFromApproveModal(approveSTFGFor);
        }}
        onReject={() => {
          if (approveSTFGFor) {
            setRejectSTFor(approveSTFGFor);
            setApproveSTFGFor(null);
          }
        }}
      />

      <RejectSTModal
        open={!!rejectSTFor}
        selectedOutward={rejectSTFor}
        onClose={() => setRejectSTFor(null)}
        onSuccess={() => {
          setRejectSTFor(null);
          refreshOutwards();
        }}
      />

      <ReturnOutwardModal
        open={!!returnOutwardFor}
        selectedOutward={returnOutwardFor}
        onClose={() => setReturnOutwardFor(null)}
        onSuccess={() => {
          setReturnOutwardFor(null);
          refreshOutwards();
        }}
      />
    </>
  );
};

export default OutwardOrdersTable;
